import React, { useMemo } from "react";
import type { HTMLAttributes } from "react";
import { cn } from "~/lib/cn";

export const Card = React.memo<
  HTMLAttributes<HTMLDivElement> & { error?: unknown }
>(({ className, error, children, ...props }) => {
  const cardClassName = useMemo(
    () =>
      cn(
        "bg-card p-6 flex flex-col gap-4 text-card-foreground rounded-lg border border-gray-100 shadow-sm transition hover:shadow-md",
        {
          "border-red-700": error,
        },
        className
      ),
    [error, className]
  );

  return (
    <div className={cardClassName} {...props}>
      {children}
    </div>
  );
});

Card.displayName = "Card";

export const CardHeader = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const headerClassName = useMemo(
      () => cn("flex flex-col gap-1", className),
      [className]
    );

    return (
      <div className={headerClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardHeader.displayName = "CardHeader";

export const CardTitle = React.memo<HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }) => {
    const titleClassName = useMemo(
      () => cn("font-heading text-xl leading-tight tracking-tight", className),
      [className]
    );

    return (
      <h3 className={titleClassName} {...props}>
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = "CardTitle";

export const CardDescription = React.memo<HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }) => {
    const descriptionClassName = useMemo(
      () => cn("text-muted-foreground text-sm", className),
      [className]
    );

    return (
      <p className={descriptionClassName} {...props}>
        {children}
      </p>
    );
  }
);

CardDescription.displayName = "CardDescription";

export const CardContent = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const contentClassName = useMemo(() => cn("", className), [className]);

    return (
      <div className={contentClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = "CardContent";

export const CardFooter = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const footerClassName = useMemo(
      () => cn("flex items-center pt-4", className),
      [className]
    );

    return (
      <div className={footerClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = "CardFooter";
